/** RFC 9457 problem details, as every Care Platform API returns them for errors. */
export interface Problem {
  type: string;
  title: string;
  status: number;
  detail?: string;
  instance?: string;
  traceId?: string;
  errors?: Array<{ pointer: string; detail: string }>;
}

function isProblem(value: unknown): value is Problem {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { title?: unknown }).title === 'string' &&
    typeof (value as { status?: unknown }).status === 'number'
  );
}

/**
 * A request the API refused or could not serve. Carries the HTTP status and, when the body was problem+json, the
 * problem itself. Bodies are plain: there is no response envelope to unwrap.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly problem: Problem | null;
  readonly correlationId: string | null;

  constructor(status: number, problem: Problem | null, correlationId: string | null = null) {
    super(problem?.detail ?? problem?.title ?? `Request failed with status ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
    this.correlationId = correlationId;
  }

  /** The operation is in the contract but not built yet. Show "not available yet", never a crash. */
  get notImplemented(): boolean {
    return this.status === 501;
  }

  get unauthorized(): boolean {
    return this.status === 401;
  }

  get notFound(): boolean {
    return this.status === 404;
  }
}

export function toApiError(response: Response, body: unknown): ApiError {
  return new ApiError(
    response.status,
    isProblem(body) ? body : null,
    response.headers.get('X-Correlation-Id'),
  );
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export function isNotImplemented(error: unknown): boolean {
  return isApiError(error) && error.notImplemented;
}
